import React from 'react';
import { Grid, InputAdornment, TextField } from '@mui/material';
import styles from './RibbonPageGroup.module.scss';
import type { IRibbonBtnConfig } from '../RibbonConfig';
import RibbonMenuButton from '../RibbonMenuButton';
import RibbonButtonSize from '../RibbonButtonSize';
import { mdiArrowDownCircleOutline, mdiArrowUpCircleOutline } from '@mdi/js';
import GoToPageController from '../../../Commands/GoToPageController';
import { apryseEventSliceCurrentPage } from '../../../../Redux/ApryseEventSlice';
import { useSelector } from 'react-redux';
import { ApryseSDK } from '../../../../pdf/ApryseSDK';

export default (props: IRibbonBtnConfig) => {
    const currentPageNumber = useSelector(apryseEventSliceCurrentPage);

    const [fieldText, setFieldText] = React.useState<string>(currentPageNumber.toString());

    // Apryse fires pageNumberUpdated multiple times during a smooth-scroll
    // animation (intermediate visible-page events + the final settled value).
    // Debouncing ensures we only update the field once the events stop firing,
    // i.e. when the viewer has actually arrived at the target page.
    // We never set fieldText proactively on button click — only the SDK event
    // is the source of truth.
    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setFieldText(currentPageNumber.toString());
        }, 150);
        return () => clearTimeout(timeout);
    }, [currentPageNumber]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldText(event.target.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleEnterKey();
        } else if (event.key === 'Escape') {
            handleEscapeKey();
        } else {
            const allowed = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                '0',
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
            ];

            if (!allowed.includes(event.key)) {
                event.preventDefault();
            }
        }
    };

    const handleEscapeKey = () => {
        setFieldText(currentPageNumber.toString());
    };

    const handleEnterKey = () => {
        if (!fieldText) {
            return;
        }
        switchPageByText();
    };

    const onBlur = () => {
        if (!fieldText) {
            setFieldText(currentPageNumber.toString());
            return;
        }
        switchPageByText();
    };

    const switchPageByText = () => {
        let pageNumber = parseInt(fieldText);

        if (pageNumber < 1) {
            pageNumber = 1;
        }

        if (pageNumber > ApryseSDK.pageCount) {
            pageNumber = ApryseSDK.pageCount;
        }

        ApryseSDK.documentViewer.setCurrentPage(pageNumber, true);
    };

    const navigatePage = (direction: 'Up' | 'Down') => {
        const offset = direction === 'Down' ? 1 : -1;
        let pageNumber = ApryseSDK.documentViewer.getCurrentPage() + offset;

        if (pageNumber < 1) pageNumber = 1;
        if (pageNumber > ApryseSDK.pageCount) pageNumber = ApryseSDK.pageCount;

        // No proactive fieldText update — the SDK's pageNumberUpdated event
        // will drive the field via the debounced useEffect above.
        ApryseSDK.documentViewer.setCurrentPage(pageNumber, true);
    };

    return (
        <Grid container direction="column" className={styles.pageGroup}>
            <Grid item>
                <RibbonMenuButton
                    className={styles.pageButton}
                    key="btnPageUp"
                    config={new GoToPageController('Up', mdiArrowUpCircleOutline, () => navigatePage('Up'))}
                    size={RibbonButtonSize.SmallIcon}
                />
                <TextField
                    className={styles.input}
                    size="small"
                    value={fieldText}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                    InputProps={{
                        type: 'numeric',
                        endAdornment: (
                            <InputAdornment
                                position="start"
                                className={styles.pages}
                                sx={{
                                    width: '40px',
                                }}
                            >
                                / {ApryseSDK.pageCount < 1 ? '' : ApryseSDK.pageCount}
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiInputBase-input': {
                            width: '40px',
                        },
                    }}
                />
                <RibbonMenuButton
                    className={styles.pageButton}
                    key="btnPageDown"
                    config={new GoToPageController('Down', mdiArrowDownCircleOutline, () => navigatePage('Down'))}
                    size={RibbonButtonSize.SmallIcon}
                />
            </Grid>
            <Grid item>
                <span className={styles.title}>{props.config.GetTitle()}</span>
            </Grid>
        </Grid>
    );
};
